// src/pages/inventory/InventoryKits.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    MenuItem,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Paper,
    Card,
    CardContent,
    InputAdornment,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Badge
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Inventory as InventoryIcon,
    CheckCircle as CheckCircleIcon,
    Category as CategoryIcon,
    Close as CloseIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Layers as LayersIcon,
    LocalOffer as TagIcon,
    Build as BuildIcon,
    DeleteOutline as RemoveIcon,
    MonetizationOn as MoneyIcon
} from '@mui/icons-material';

const InventoryKits = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [kits, setKits] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [categories, setCategories] = useState([]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    // Kit form state
    const [currentKit, setCurrentKit] = useState({
        id: '',
        name: '',
        description: '',
        category_id: '',
        combo_sale_price: '',
        combo_rental_price: '',
        items: [] // array of { item_id, quantity, unit_price }
    });

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            const [kitsRes, itemsRes, catRes] = await Promise.all([
                fetch('/api/inventory/kits', { headers }),
                fetch('/api/inventory/items', { headers }),
                fetch('/api/inventory/categories', { headers })
            ]);

            if (kitsRes.ok) setKits(await kitsRes.json());
            if (itemsRes.ok) setInventoryItems(await itemsRes.json());
            if (catRes.ok) setCategories(await catRes.json());

        } catch (err) {
            console.error('Error fetching inventory kits:', err);
            setError('Error al cargar la lista de packs de instalación.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenDialog = (kit = null) => {
        if (kit) {
            setCurrentKit({
                id: kit.id,
                name: kit.name || '',
                description: kit.description || '',
                category_id: kit.category_id || '',
                combo_sale_price: kit.combo_sale_price ? parseFloat(kit.combo_sale_price) : '',
                combo_rental_price: kit.combo_rental_price ? parseFloat(kit.combo_rental_price) : '',
                items: kit.items ? kit.items.map(i => ({
                    item_id: i.item_id,
                    quantity: i.quantity || 1,
                    unit_price: i.kit_unit_price || i.selling_price || 0
                })) : []
            });
            setIsEditing(true);
        } else {
            setCurrentKit({
                id: '',
                name: '',
                description: '',
                category_id: '',
                combo_sale_price: '',
                combo_rental_price: '',
                items: [
                    { item_id: '', quantity: 1, unit_price: 0 }
                ]
            });
            setIsEditing(false);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentKit({
            id: '',
            name: '',
            description: '',
            category_id: '',
            combo_sale_price: '',
            combo_rental_price: '',
            items: []
        });
    };

    const handleAddItemToKit = () => {
        setCurrentKit(prev => ({
            ...prev,
            items: [...prev.items, { item_id: '', quantity: 1, unit_price: 0 }]
        }));
    };

    const handleRemoveItemFromKit = (index) => {
        setCurrentKit(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handleKitItemChange = (index, field, value) => {
        setCurrentKit(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = {
                ...updatedItems[index],
                [field]: value
            };

            // Auto populate default price if item_id changed
            if (field === 'item_id') {
                const foundItem = inventoryItems.find(i => i.id === value);
                if (foundItem) {
                    updatedItems[index].unit_price = parseFloat(foundItem.selling_price) || 0;
                }
            }

            return { ...prev, items: updatedItems };
        });
    };

    const handleSubmit = async () => {
        try {
            setError(null);
            if (!currentKit.name.trim()) {
                setError('El nombre del pack es obligatorio.');
                return;
            }

            if (currentKit.items.length === 0 || currentKit.items.some(i => !i.item_id)) {
                setError('Seleccione al menos un producto válido para el pack.');
                return;
            }

            setLoading(true);
            const token = localStorage.getItem('token');
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing ? `/api/inventory/kits/${currentKit.id}` : '/api/inventory/kits';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(currentKit)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || 'Error al guardar el pack');
            }

            setSuccess(`Pack de instalación ${isEditing ? 'actualizado' : 'creado'} con éxito.`);
            setTimeout(() => setSuccess(null), 4000);
            handleCloseDialog();
            await fetchData();
        } catch (err) {
            console.error(err);
            setError(err.message || 'Error al procesar el pack.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteKit = async (id) => {
        if (!window.confirm('¿Está seguro de desactivar este pack de instalación?')) {
            return;
        }

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/inventory/kits/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!res.ok) {
                throw new Error('Error al eliminar pack');
            }

            setSuccess('Pack desactivado con éxito.');
            setTimeout(() => setSuccess(null), 4000);
            await fetchData();
        } catch (err) {
            console.error(err);
            setError(err.message || 'Error al eliminar pack.');
        } finally {
            setLoading(false);
        }
    };

    const filteredKits = kits.filter(k => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
            (k.name && k.name.toLowerCase().includes(s)) ||
            (k.description && k.description.toLowerCase().includes(s)) ||
            (k.category_name && k.category_name.toLowerCase().includes(s))
        );
    });

    if (loading && kits.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '65vh', gap: 2 }}>
                <CircularProgress size={48} thickness={4} sx={{ color: '#0284c7' }} />
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Cargando packs y kits de instalación...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3.5 }, bgcolor: '#f8fafc', minHeight: '100vh', width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Header Hero Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                    <Box sx={{
                        width: { xs: 44, md: 52 },
                        height: { xs: 44, md: 52 },
                        borderRadius: '14px',
                        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        boxShadow: '0 8px 16px rgba(2, 132, 199, 0.25)',
                        flexShrink: 0
                    }}>
                        <LayersIcon sx={{ fontSize: { xs: 24, md: 28 } }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                                Packs y Kits de Instalación
                            </Typography>
                            <Chip
                                label={`${kits.length} Packs Activos`}
                                size="small"
                                sx={{ bgcolor: 'rgba(2, 132, 199, 0.1)', color: '#0284c7', fontWeight: 800, fontSize: '11px', borderRadius: '6px' }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: 0.2, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                            Agrupa equipos e insumos (ONU + Router + Cables) con precio combo de Venta y Alquiler
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        variant="outlined"
                        onClick={() => fetchData(true)}
                        disabled={refreshing}
                        startIcon={<RefreshIcon className={refreshing ? 'spin' : ''} />}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: '#cbd5e1', color: '#475569' }}
                    >
                        Refrescar
                    </Button>

                    <Button
                        variant="contained"
                        onClick={() => handleOpenDialog()}
                        startIcon={<AddIcon />}
                        sx={{
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                            boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)',
                            flex: { xs: 1, sm: 'initial' }
                        }}
                    >
                        Crear Nuevo Pack
                    </Button>
                </Box>
            </Box>

            {/* Filter Search Bar */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Buscar pack por nombre, descripción o categoría..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                sx: { borderRadius: '10px', bgcolor: '#fff' }
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Kits Cards Grid */}
            <Grid container spacing={2.5}>
                {filteredKits.length > 0 ? (
                    filteredKits.map((kit) => {
                        const regularSale = kit.regular_total_price || 0;
                        const regularRental = kit.regular_rental_price || 0;
                        const comboSale = parseFloat(kit.combo_sale_price) || 0;
                        const comboRental = parseFloat(kit.combo_rental_price) || 0;

                        return (
                            <Grid item xs={12} sm={6} md={4} key={kit.id}>
                                <Card sx={{
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                                    }
                                }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        {/* Pack Header */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                                                    {kit.name}
                                                </Typography>
                                                {kit.category_name && (
                                                    <Chip
                                                        label={kit.category_name}
                                                        size="small"
                                                        sx={{ height: 20, fontSize: '10px', fontWeight: 700, bgcolor: '#f0f9ff', color: '#0369a1', mt: 0.5 }}
                                                    />
                                                )}
                                            </Box>

                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <IconButton size="small" onClick={() => handleOpenDialog(kit)} sx={{ color: '#0284c7', bgcolor: 'rgba(2, 132, 199, 0.08)' }}>
                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDeleteKit(kit.id)} sx={{ color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.08)' }}>
                                                    <DeleteIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '12px', mb: 2, minHeight: 36 }}>
                                            {kit.description || 'Sin descripción detallada.'}
                                        </Typography>

                                        {/* Included Items List */}
                                        <Paper sx={{ p: 1.5, bg: '#f8fafc', borderRadius: '10px', mb: 2, border: '1px solid #f1f5f9' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#334155', display: 'block', mb: 0.8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                Productos Incluidos ({kit.items ? kit.items.length : 0})
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                                                {kit.items && kit.items.length > 0 ? (
                                                    kit.items.map((item, idx) => (
                                                        <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>
                                                                • {item.quantity}x {item.item_name}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                                                                S/ {(parseFloat(item.selling_price || 0) * item.quantity).toFixed(2)}
                                                            </Typography>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>Sin ítems configurados</Typography>
                                                )}
                                            </Box>
                                        </Paper>

                                        <Divider sx={{ my: 1.5 }} />

                                        {/* Combo Prices Summary */}
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Box sx={{ p: 1, bgcolor: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                                    <Typography variant="caption" sx={{ color: '#166534', fontWeight: 700, display: 'block', fontSize: '10px' }}>
                                                        PRECIO COMBO VENTA
                                                    </Typography>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#15803d' }}>
                                                        S/ {comboSale > 0 ? comboSale.toFixed(2) : regularSale.toFixed(2)}
                                                    </Typography>
                                                    {regularSale > comboSale && comboSale > 0 && (
                                                        <Typography variant="caption" sx={{ color: '#94a3b8', textDecoration: 'line-through', fontSize: '10px' }}>
                                                            Reg: S/ {regularSale.toFixed(2)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Grid>

                                            <Grid item xs={6}>
                                                <Box sx={{ p: 1, bgcolor: '#faf5ff', borderRadius: '8px', border: '1px solid #e9d5ff' }}>
                                                    <Typography variant="caption" sx={{ color: '#6b21a8', fontWeight: 700, display: 'block', fontSize: '10px' }}>
                                                        ALQUILER COMBO (MES)
                                                    </Typography>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#7c3aed' }}>
                                                        S/ {comboRental > 0 ? comboRental.toFixed(2) : regularRental.toFixed(2)}
                                                    </Typography>
                                                    {regularRental > comboRental && comboRental > 0 && (
                                                        <Typography variant="caption" sx={{ color: '#94a3b8', textDecoration: 'line-through', fontSize: '10px' }}>
                                                            Reg: S/ {regularRental.toFixed(2)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })
                ) : (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: '16px', border: '1px dashed #cbd5e1' }}>
                            <LayersIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#334155' }}>
                                No se encontraron packs de instalación
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                                Crea packs preconfigurados (ej. Kit Fibra 100M: ONU GPON + Router Dual Band + Cables) para agilizar instalaciones.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => handleOpenDialog()}
                                startIcon={<AddIcon />}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, bgcolor: '#0284c7' }}
                            >
                                Crear Primer Pack
                            </Button>
                        </Paper>
                    </Grid>
                )}
            </Grid>

            {/* Modal Dialog for Add / Edit Pack */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px', margin: { xs: 1.5, sm: 2 } } }}>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: 'rgba(2, 132, 199, 0.1)', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <LayersIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.25rem' } }}>
                            {isEditing ? 'Editar Pack de Instalación' : 'Nuevo Pack / Kit de Instalación'}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ pt: 1 }}>
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Nombre del Pack / Kit"
                                    value={currentKit.name}
                                    onChange={(e) => setCurrentKit(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Ej. Kit Fibra Hogar 200M (ONU + Router AC + Cable)"
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Categoría"
                                    value={currentKit.category_id}
                                    onChange={(e) => setCurrentKit(prev => ({ ...prev, category_id: e.target.value }))}
                                >
                                    <MenuItem value="">Sin categoría</MenuItem>
                                    {categories.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={2}
                                    label="Descripción del Combo / Observaciones"
                                    value={currentKit.description}
                                    onChange={(e) => setCurrentKit(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Describe qué incluye el pack o para qué tipo de cliente aplica..."
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 0.5 }}>
                                    <Chip label="Productos Integrantes del Pack" size="small" sx={{ fontWeight: 700, fontSize: '11px' }} />
                                </Divider>
                            </Grid>

                            {/* Kit Items List */}
                            <Grid item xs={12}>
                                {currentKit.items.map((item, index) => (
                                    <Box key={index} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <TextField
                                            select
                                            size="small"
                                            label="Producto"
                                            value={item.item_id}
                                            onChange={(e) => handleKitItemChange(index, 'item_id', e.target.value)}
                                            sx={{ flex: 2, minWidth: 200 }}
                                        >
                                            <MenuItem value="">Seleccionar producto</MenuItem>
                                            {inventoryItems.map(invItem => (
                                                <MenuItem key={invItem.id} value={invItem.id}>
                                                    {invItem.name} {invItem.brand ? `(${invItem.brand})` : ''} - S/ {parseFloat(invItem.selling_price || 0).toFixed(2)}
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            type="number"
                                            size="small"
                                            label="Cant."
                                            value={item.quantity}
                                            onChange={(e) => handleKitItemChange(index, 'quantity', e.target.value)}
                                            sx={{ width: 80 }}
                                        />

                                        <TextField
                                            type="number"
                                            size="small"
                                            label="Precio Unit. S/"
                                            value={item.unit_price}
                                            onChange={(e) => handleKitItemChange(index, 'unit_price', e.target.value)}
                                            sx={{ width: 120 }}
                                        />

                                        <IconButton size="small" color="error" onClick={() => handleRemoveItemFromKit(index)}>
                                            <RemoveIcon />
                                        </IconButton>
                                    </Box>
                                ))}

                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleAddItemToKit}
                                    startIcon={<AddIcon />}
                                    sx={{ mt: 1, borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
                                >
                                    Agregar Producto al Pack
                                </Button>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 0.5 }}>
                                    <Chip label="Precios Especiales de Combo" size="small" sx={{ fontWeight: 700, fontSize: '11px' }} />
                                </Divider>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Precio Venta Combo (S/)"
                                    value={currentKit.combo_sale_price}
                                    onChange={(e) => setCurrentKit(prev => ({ ...prev, combo_sale_price: e.target.value }))}
                                    helperText="Precio especial si el cliente lo compra completo"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">S/</InputAdornment>
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Precio Alquiler Combo / Mes (S/)"
                                    value={currentKit.combo_rental_price}
                                    onChange={(e) => setCurrentKit(prev => ({ ...prev, combo_rental_price: e.target.value }))}
                                    helperText="Canon mensual de alquiler si el pack se alquila"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">S/</InputAdornment>
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleCloseDialog} sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 800,
                            px: 3
                        }}
                    >
                        {isEditing ? 'Guardar Cambios' : 'Crear Pack'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InventoryKits;
